<template>
  <Loading
    v-if="composable.loading.setting.isShow"
    :content="composable.loading.setting.content"
  />

  <div
    v-if="page === 'initialization'"
    class="page page--no-scroll"
  />

  <div
    v-else-if="page==='chatting'"
    class="page"
  >
    <div class="chat">
      <div class="mt2 pb3 fs5 fw4 fc--primary">
        {{ clinicInfo.fullname }}
      </div>
      <div class="chat__content">
        <template
          v-for="message in allMessages"
          :key="message"
        >
          <div
            v-if="message.sender!=='member'"
            class="row--start"
          >
            <div
              v-if="message.type==='text'"
              class="
              page__text ba--primary border-radius-2
              pv2 ph3 fc--primary align--left mh0
              "
            >
              {{ message.content }}
            </div>
            <img
              v-else-if="message.type==='picture'"
              :src="message.url"
              @load="scrollToBottom"
              alt=""
              class="image--size-x-10 border-radius-2"
            >
            <div class="column--end fs1 ml2">
              <div class="align--left mb1" />
              <div class="align--left">
                {{ moment(message.timestamp).format('MM/DD hh:mm') }}
              </div>
            </div>
          </div>
          <div
            v-else
            class="row--end"
          >
            <div class="column--end fs1 mr2">
              <div
                v-if="message.read"
                class="align--right mb1"
              >
                已讀
              </div>
              <div class="align--right">
                {{ moment(message.timestamp).format('MM/DD HH:mm') }}
              </div>
            </div>
            <div
              v-if="message.type==='text'"
              class="
              page__text bc--primary border-radius-2 pv2 ph3
              fc--white align--left mh0
              "
            >
              {{ message.content }}
            </div>
            <img
              v-else-if="message.type==='picture'"
              :src="message.url"
              @load="scrollToBottom"
              alt=""
              class="image--size-x-10 border-radius-2"
            >
          </div>
        </template>
        <div
          v-if="chatLoading.isShow"
          class="row--end"
        >
          <div class="chat-loading mr4" />
        </div>
      </div>
      <div class="chat__footer pv3">
        <div class="column--central">
          <label class="label label--secondary column--central mr3">
            <font-awesome-icon :icon="['fas', 'photo-video']" />
            <input
              @change="sendPicture($event)"
              type="file"
              accept="image/*"
              style="display: none"
            >
          </label>
        </div>
        <textarea
          v-model.trim="inputMessage"
          class="pv1 ph2"
        />
        <div class="column--central">
          <div
            @click="sendText"
            class="label label--primary column--central ml3"
          >
            <font-awesome-icon :icon="['fas', 'paper-plane']" />
          </div>
        </div>
      </div>
    </div>
  </div>

  <Death
    v-else
    :message="composable.death.message"
    :page="page"
  />

  <Modal
    v-if="composable.modal.setting.isShow"
    :setting="composable.modal.setting"
    @close="composable.modal.close"
    @confirm="cancelAppointment"
  />
</template>

<script>
import {
  ref, onMounted, nextTick,
} from 'vue';
import { useRoute } from 'vue-router';
import spiderman from '@/spiderman/index';
import composable from '@/composable/index';
import api from '@/api/index';
import chatLoading from './composable/chat-loading';

export default {
  setup() {
    const route = useRoute();
    const page = ref('initialization');
    const clinicInfo = ref(null);
    const allMessages = ref([]);
    const inputMessage = ref('');
    let socket;

    onMounted(async () => {
      try {
        composable.loading.show('資料載入中');

        const { clinic } = route.params;
        if (clinic === 'heart') {
          await composable.member.initialize({
            liffId: '1656042583-2zYBaZr4',
            clinic: '金安心',
          });
        } else if (clinic === 'beauty') {
          await composable.member.initialize({
            liffId: '1656042583-DxXb9pdG',
            clinic: '金禾美',
          });
        }
        clinicInfo.value = spiderman.clinic[composable.member.info.clinic];

        connectWebSocket();
        page.value = 'chatting';
      } catch (error) {
        page.value = 'death';
        composable.death.show(error);
      }
    });

    function connectWebSocket() {
      socket = new WebSocket(api.chatSocket);

      socket.onopen = () => {
        const { clinic, lineId } = composable.member.info;
        sendToServer({
          request: 'memberSetup',
          body: {
            clinic,
            lineId,
          },
        });
      };

      socket.onmessage = async (receivedMessage) => {
        const { response, data } = JSON.parse(receivedMessage.data);

        switch (response) {
          case 'messageHistory':
            allMessages.value = data;
            scrollToBottom();
            composable.loading.close();

            break;

          case 'newMessage':
            allMessages.value.push(data);
            scrollToBottom();

            break;

          case 'mgmtUserAlreadyRead':
            allMessages.value.forEach((message, index) => {
              if (message.sender === 'member') {
                allMessages.value[index].read = true;
              }
            });
            break;

          default:
            break;
        }
      };
    }

    function sendToServer(obj) {
      socket.send(JSON.stringify(obj));
    }

    async function sendText() {
      if (inputMessage.value.trim() === '') return;

      const { clinic, lineId } = composable.member.info;
      const body = {
        clinic,
        lineId,
        sender: 'member',
        type: 'text',
        content: inputMessage.value,
        read: false,
        timestamp: new Date(),
      };

      allMessages.value.push(body);
      inputMessage.value = '';
      scrollToBottom();

      sendToServer({
        request: 'memberSendMessage',
        body,
      });
    }

    async function sendPicture(element) {
      try {
        chatLoading.show();
        scrollToBottom();

        if (element.target.files[0].size > 5 * 1024 * 1024) throw new Error('檔案大小已超過上限(5MB)');

        const formData = spiderman.generate.formData({
          picture: element.target.files[0],
        });

        const { key, url } = await api.chatPictures.sendToS3(
          composable.member, formData,
        );

        const { clinic, lineId } = composable.member.info;
        const body = {
          clinic,
          lineId,
          sender: 'member',
          type: 'picture',
          key,
          url,
          read: false,
          timestamp: new Date(),
        };
        allMessages.value.push(body);

        sendToServer({
          request: 'memberSendMessage',
          body,
        });
      } catch (error) {
        composable.modal.alert(error);
      } finally {
        chatLoading.close();
        scrollToBottom();
      }
    }

    async function scrollToBottom() {
      const chatContent = document.getElementsByClassName('chat__content')[0];
      // 等待更改都render到畫面
      await nextTick();

      chatContent.scroll({
        top: chatContent.scrollHeight,
        behavior: 'smooth',
      });
    }

    return {
      liff: spiderman.liff,
      moment: spiderman.moment,
      composable,
      page,
      clinicInfo,
      allMessages,
      inputMessage,
      sendText,
      sendPicture,
      scrollToBottom,
      chatLoading,
    };
  },
};
</script>
